'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { downloadBlob } from '../lib/api/dashboard';
import { resolveAnalyticalYearPeriod } from '../lib/api/analytical-periods';
import { businessReportRoute } from '../lib/business-report-routes';
import {
  exportBusinessOperationalEvidence,
  exportBusinessDailyRevenue,
  getBusinessOperationalEvidence,
  getBusinessDailyRevenue,
  type BusinessOperationalEvidence,
  type BusinessOperationalEvidenceMetric,
  type BusinessDailyRevenueCenter,
  type BusinessDailyRevenueMetric,
  type BusinessDailyRevenueRatio,
  type BusinessDailyRevenueResponse,
  type BusinessMoneyPresentation,
  type BusinessDailyRevenueFilters,
} from '../lib/api/business-reports';

type MetricRow = {
  key: string;
  label: string;
  shortLabel: string;
  kind: 'money' | 'count' | 'ratio';
  value: BusinessDailyRevenueMetric | BusinessDailyRevenueRatio;
};
type RevenuePeriodMode = BusinessDailyRevenueFilters['mode'] | 'YTD' | 'FY_YTD';

const TODAY_IST = todayInIndia();
const DEFAULT_REPORT_DATE_IST = previousIsoDate(TODAY_IST);

export function BusinessDailyRevenueTab({
  active,
  token,
  canExport,
  canViewSensitiveEvidence,
  canExportSensitiveEvidence,
  canViewReconciliation,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  canExport: boolean;
  canViewSensitiveEvidence: boolean;
  canExportSensitiveEvidence: boolean;
  canViewReconciliation: boolean;
  onTopBarActionsChange?: (actions: ReactNode | null) => void;
}) {
  const [draftAsOf, setDraftAsOf] = useState(DEFAULT_REPORT_DATE_IST);
  const [draftMode, setDraftMode] = useState<RevenuePeriodMode>('CURRENT_MONTH');
  const [draftDate, setDraftDate] = useState(DEFAULT_REPORT_DATE_IST);
  const [draftMonth, setDraftMonth] = useState(DEFAULT_REPORT_DATE_IST.slice(0, 7));
  const [draftFrom, setDraftFrom] = useState(`${DEFAULT_REPORT_DATE_IST.slice(0, 7)}-01`);
  const [draftTo, setDraftTo] = useState(DEFAULT_REPORT_DATE_IST);
  const [draftCenters, setDraftCenters] = useState<string[]>([]);
  const [appliedAsOf, setAppliedAsOf] = useState(DEFAULT_REPORT_DATE_IST);
  const [appliedMode, setAppliedMode] = useState<RevenuePeriodMode>('CURRENT_MONTH');
  const [appliedDate, setAppliedDate] = useState(DEFAULT_REPORT_DATE_IST);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_DATE_IST.slice(0, 7));
  const [appliedFrom, setAppliedFrom] = useState(`${DEFAULT_REPORT_DATE_IST.slice(0, 7)}-01`);
  const [appliedTo, setAppliedTo] = useState(DEFAULT_REPORT_DATE_IST);
  const [appliedCenters, setAppliedCenters] = useState<string[]>([]);
  const [report, setReport] = useState<BusinessDailyRevenueResponse | null>(null);
  const [availableCenters, setAvailableCenters] = useState<
    Array<{ centerCode: string; displayName: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<{
    title: string;
    message: string;
    activationPending: boolean;
  } | null>(null);
  const centrePickerRef = useRef<HTMLDetailsElement | null>(null);

  const closeCentrePicker = useCallback(() => {
    centrePickerRef.current?.removeAttribute('open');
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const picker = centrePickerRef.current;
      if (picker?.open && event.target instanceof Node && !picker.contains(event.target)) {
        picker.removeAttribute('open');
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      const picker = centrePickerRef.current;
      if (event.key !== 'Escape' || !picker?.open) return;
      picker.removeAttribute('open');
      picker.querySelector('summary')?.focus();
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [active]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBusinessDailyRevenue(token, revenueFilters(appliedMode, appliedAsOf, appliedDate, appliedMonth, appliedFrom, appliedTo, appliedCenters));
      setReport(result);
      setAvailableCenters((current) => mergeCenters(
        current,
        result.report.centers,
        appliedCenters.length === 0,
      ));
    } catch (loadError) {
      const normalized = reportError(loadError);
      setReport(null);
      setError(normalized);
    } finally {
      setLoading(false);
    }
  }, [appliedAsOf, appliedCenters, appliedDate, appliedFrom, appliedMode, appliedMonth, appliedTo, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const applyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    closeCentrePicker();
    const selectedDates = [draftAsOf, draftDate, draftFrom, draftTo].filter(Boolean);
    if (selectedDates.some((date) => date > TODAY_IST) || (draftMode === 'CUSTOM_RANGE' && draftTo < draftFrom)) {
      setError({
        title: 'Check the report date',
        message: 'Choose a valid as-of date that is not in the future.',
        activationPending: false,
      });
      return;
    }
    let nextFrom = draftFrom;
    let nextTo = draftTo;
    if (draftMode === 'YTD' || draftMode === 'FY_YTD') {
      try {
        const range = await resolveAnalyticalYearPeriod(draftMode, token, draftAsOf);
        nextFrom = range.from;
        nextTo = range.to;
        setDraftFrom(range.from);
        setDraftTo(range.to);
      } catch (caught) {
        setError({ title: 'Reporting period unavailable', message: caught instanceof Error ? caught.message : 'Unable to resolve the selected reporting period.', activationPending: false });
        return;
      }
    }
    setAppliedAsOf(draftAsOf);
    setAppliedMode(draftMode); setAppliedDate(draftDate); setAppliedMonth(draftMonth); setAppliedFrom(nextFrom); setAppliedTo(nextTo); setAppliedCenters(draftCenters);
    if (draftAsOf === appliedAsOf && draftMode === appliedMode && draftDate === appliedDate && draftMonth === appliedMonth && draftFrom === appliedFrom && draftTo === appliedTo && draftCenters.join() === appliedCenters.join()) {
      void load();
    }
  };

  const clearFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    closeCentrePicker();
    const alreadyAppliedDefaults =
      appliedAsOf === DEFAULT_REPORT_DATE_IST &&
      appliedMode === 'CURRENT_MONTH' &&
      appliedDate === DEFAULT_REPORT_DATE_IST &&
      appliedMonth === DEFAULT_REPORT_DATE_IST.slice(0, 7) &&
      appliedFrom === `${DEFAULT_REPORT_DATE_IST.slice(0, 7)}-01` &&
      appliedTo === DEFAULT_REPORT_DATE_IST &&
      appliedCenters.length === 0;
    setDraftAsOf(DEFAULT_REPORT_DATE_IST);
    setDraftMode('CURRENT_MONTH');
    setDraftDate(DEFAULT_REPORT_DATE_IST);
    setDraftMonth(DEFAULT_REPORT_DATE_IST.slice(0, 7));
    setDraftFrom(`${DEFAULT_REPORT_DATE_IST.slice(0, 7)}-01`);
    setDraftTo(DEFAULT_REPORT_DATE_IST);
    setDraftCenters([]);
    setAppliedAsOf(DEFAULT_REPORT_DATE_IST);
    setAppliedMode('CURRENT_MONTH');
    setAppliedDate(DEFAULT_REPORT_DATE_IST);
    setAppliedMonth(DEFAULT_REPORT_DATE_IST.slice(0, 7));
    setAppliedFrom(`${DEFAULT_REPORT_DATE_IST.slice(0, 7)}-01`);
    setAppliedTo(DEFAULT_REPORT_DATE_IST);
    setAppliedCenters([]);
    if (alreadyAppliedDefaults) void load();
  };

  const exportExcel = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const result = await exportBusinessDailyRevenue(token, revenueFilters(appliedMode, appliedAsOf, appliedDate, appliedMonth, appliedFrom, appliedTo, appliedCenters));
      downloadBlob(
        result.blob,
        result.fileName ||
          `Ferty9_Daily_Revenue_Snapshot_${appliedAsOf}.xlsx`,
      );
    } catch (exportError) {
      setError(reportError(exportError, 'Could not export this report'));
    } finally {
      setExporting(false);
    }
  }, [appliedAsOf, appliedCenters, appliedDate, appliedFrom, appliedMode, appliedMonth, appliedTo, token]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;

    if (!canExport) {
      onTopBarActionsChange(null);
      return () => onTopBarActionsChange(null);
    }

    const developmentPreviewActive = report?.developmentPreview?.active === true;
    const reconciliationParams = new URLSearchParams({
      tab: 'reconciliation',
      from: report?.selection.from || appliedFrom,
      to: report?.selection.to || appliedTo,
      ...(appliedCenters.length === 1 ? { center: appliedCenters[0] } : {}),
    });
    onTopBarActionsChange(
      <>
      {canExport && canExportSensitiveEvidence && canViewReconciliation && <button
        className="btn btn-outline compact-btn"
        type="button"
        onClick={() => { window.location.href = businessReportRoute('revenue-operations', reconciliationParams.toString()); }}
      >Invoice reconciliation</button>}
      <button
        className="btn btn-primary compact-btn"
        type="button"
        onClick={() => void exportExcel()}
        disabled={loading || exporting || developmentPreviewActive}
        aria-label="Export Daily Revenue Snapshot to Excel"
        title={developmentPreviewActive ? 'Excel export remains behind production activation.' : undefined}
      >
        <DownloadIcon />
        {exporting ? 'Preparing Excel…' : developmentPreviewActive ? 'Export pending activation' : 'Export Excel'}
      </button>
      </>,
    );

    return () => onTopBarActionsChange(null);
  }, [active, appliedCenters, appliedFrom, appliedTo, canExport, canExportSensitiveEvidence, canViewReconciliation, exportExcel, exporting, loading, onTopBarActionsChange, report?.developmentPreview?.active, report?.selection.from, report?.selection.to]);

  const grandRevenue = report?.report.grandTotal.metrics.revenueRupees;
  const kpis = useMemo(
    () => (report?.selection.actualOnly && report.rangeActuals
      ? [{ label: 'Actual revenue', value: formatValue(report.rangeActuals.totalRevenueRupees, 'money', report.currency, report.moneyPresentation), note: `${displayDate(report.selection.from)} to ${displayDate(report.selection.to)}` },
        { label: 'Monthly target', value: 'Not applicable', note: 'Actual-only selection' },
        { label: 'Projected landing', value: 'Not applicable', note: 'Actual-only selection' }]
      : report && grandRevenue
      ? [
          {
            label: `${report.report.periods.previousMonth.label} revenue`,
            value: formatValue(
              grandRevenue.previousMonthActual,
              'money',
              report.currency,
              report.moneyPresentation,
            ),
            note: 'Completed previous month',
          },
          {
            label: 'Internal target',
            value: formatValue(
              grandRevenue.target,
              'money',
              report.currency,
              report.moneyPresentation,
            ),
            note: report.report.periods.currentMonth.label,
          },
          {
            label: 'MTD achieved',
            value: formatValue(
              grandRevenue.mtdActual,
              'money',
              report.currency,
              report.moneyPresentation,
            ),
            note: `Through ${displayDate(report.report.asOfDate)}`,
          },
          {
            label: 'Projected landing',
            value: formatValue(
              grandRevenue.projectedLanding,
              'money',
              report.currency,
              report.moneyPresentation,
            ),
            note:
              landingSourceLabel(grandRevenue.landingSource),
          },
          {
            label: 'Growth vs LM %',
            value: formatGrowthPercent(grandRevenue.growthVsPreviousMonth).text,
            note: `${growthLabel(grandRevenue.growthVsPreviousMonth)} · vs ${report.report.periods.previousMonthAligned.label}`,
            tone: formatGrowthPercent(grandRevenue.growthVsPreviousMonth).tone,
          },
          {
            label: 'Growth vs Q4 %',
            value: formatGrowthPercent(grandRevenue.growthVsQ4).text,
            note: growthLabel(grandRevenue.growthVsQ4),
            tone: formatGrowthPercent(grandRevenue.growthVsQ4).tone,
          },
        ]
      : []),
    [grandRevenue, report],
  );

  if (!active) return null;

  return (
    <section
      className="tab-content business-daily-revenue"
      id="tab-business-daily-revenue"
      aria-labelledby="page-title"
      aria-busy={loading}
    >
      <form className="card enterprise-filter-card daily-revenue-filter-card" onSubmit={applyFilters}>
        <div className="enterprise-filter-grid daily-revenue-filter-grid">
        <label className="enterprise-field daily-revenue-period-field">
          <span>Period</span>
          <span className="daily-revenue-select-control">
            <select value={draftMode} onChange={(event) => setDraftMode(event.target.value as RevenuePeriodMode)}>
              <option value="CURRENT_MONTH">Current-month snapshot</option>
              <option value="SPECIFIC_DATE">Specific date</option>
              <option value="CALENDAR_MONTH">Calendar month</option>
              <option value="YTD">YTD (Year to Date)</option>
              <option value="FY_YTD">FY-YTD (Financial Year to Date)</option>
              <option value="CUSTOM_RANGE">Custom range</option>
            </select>
            <i className="daily-revenue-select-caret" aria-hidden="true" />
          </span>
        </label>
        {(draftMode === 'CURRENT_MONTH' || draftMode === 'YTD' || draftMode === 'FY_YTD') && (
        <label className="enterprise-field">
          <span>As-of date</span>
          <input
            type="date"
            value={draftAsOf}
            max={TODAY_IST}
            onChange={(event) => setDraftAsOf(event.target.value)}
            required
          />
        </label>
        )}
        {draftMode === 'SPECIFIC_DATE' && <label className="enterprise-field"><span>Business date</span><input type="date" value={draftDate} max={TODAY_IST} onChange={(event) => setDraftDate(event.target.value)} required /></label>}
        {draftMode === 'CALENDAR_MONTH' && <label className="enterprise-field"><span>Calendar month</span><input type="month" value={draftMonth} max={TODAY_IST.slice(0, 7)} onChange={(event) => setDraftMonth(event.target.value)} required /></label>}
        {draftMode === 'CUSTOM_RANGE' && <><label className="enterprise-field"><span>From</span><input type="date" value={draftFrom} max={TODAY_IST} onChange={(event) => setDraftFrom(event.target.value)} required /></label><label className="enterprise-field"><span>To</span><input type="date" value={draftTo} min={draftFrom} max={TODAY_IST} onChange={(event) => setDraftTo(event.target.value)} required /></label></>}
        <div className="enterprise-field daily-revenue-centre-field">
          <span>Centres</span>
          <details className="daily-revenue-centre-picker" ref={centrePickerRef}>
            <summary aria-label="Centre filter" aria-haspopup="listbox">
              {draftCenters.length === 0
                ? 'All permitted centres'
                : `${draftCenters.length} centre${draftCenters.length === 1 ? '' : 's'} selected`}
            </summary>
            <div className="daily-revenue-centre-menu">
              <div className="daily-revenue-centre-menu-header">
                <span>Select centres</span>
                <button type="button" onClick={() => setDraftCenters([])}>All centres</button>
              </div>
              <div className="daily-revenue-centre-options">
                {availableCenters.map((center) => (
                  <label key={center.centerCode}>
                    <input
                      type="checkbox"
                      checked={draftCenters.includes(center.centerCode)}
                      onChange={(event) => setDraftCenters((current) => event.target.checked
                        ? [...current, center.centerCode]
                        : current.filter((code) => code !== center.centerCode))}
                    />
                    <span>{center.displayName}</span>
                    <small>{center.centerCode}</small>
                  </label>
                ))}
              </div>
            </div>
          </details>
        </div>
        <div className="daily-revenue-filter-actions">
          <button className="btn btn-outline compact-btn filter-action" type="button" onClick={clearFilters} disabled={loading}>Clear Filters</button>
          <button className="btn btn-primary compact-btn filter-action" type="submit" disabled={loading}>
            {loading ? 'Loading…' : 'Apply Filters'}
          </button>
        </div>
        </div>
      </form>

      <div className="daily-revenue-live-region" aria-live="polite">
        {loading ? 'Loading Daily Revenue Snapshot.' : ''}
      </div>

      {loading && !report && <DailyRevenueSkeleton />}

      {error && !loading && (
        <div
          className={`daily-revenue-state ${error.activationPending ? 'pending' : 'error'}`}
          role="alert"
        >
          <span className="daily-revenue-state-icon" aria-hidden="true">
            {error.activationPending ? 'i' : '!'}
          </span>
          <div>
            <h3>{error.title}</h3>
            <p>{error.message}</p>
            <button className="btn btn-outline" type="button" onClick={() => void load()}>
              Try again
            </button>
          </div>
        </div>
      )}

      {report && !loading && <SourceCoverageNotice report={report} />}

      {report && (
        <>
          <section aria-labelledby="daily-revenue-kpi-heading">
            <div className="daily-revenue-section-heading">
              <div>
                <h3 id="daily-revenue-kpi-heading">Management overview</h3>
              </div>
              <span className="daily-revenue-overview-meta">
                <ReportDataStatus report={report} />
                <b aria-hidden="true">|</b>
                <span><strong>{report.report.centers.length}</strong> permitted centre{report.report.centers.length === 1 ? '' : 's'}</span>
                <b aria-hidden="true">|</b>
                <span>Currency <strong>{report.currency}</strong> · values shown in <strong>{displayScaleLabel(report.moneyPresentation.summaryDisplayScale)}</strong></span>
              </span>
            </div>
            <div className="daily-revenue-kpis">
              {kpis.map((kpi) => (
                <article
                  className={`daily-revenue-kpi ${kpi.tone || ''}`}
                  key={kpi.label}
                >
                  <span>{kpi.label}</span>
                  <strong>{kpi.value}</strong>
                  <small>{kpi.note}</small>
                </article>
              ))}
            </div>
          </section>

          {report.status === 'NO_DATA' || report.report.centers.length === 0 ? (
            <div className="daily-revenue-state empty">
              <span className="daily-revenue-state-icon" aria-hidden="true">0</span>
              <div>
                <h3>No report data for this selection</h3>
                <p>
                  No validated financial facts or approved operational rows are
                  available for the selected date and permitted centre scope.
                </p>
              </div>
            </div>
          ) : (
            report.selection.actualOnly && report.rangeActuals ? <ActualRangeTable report={report} /> : <RevenueTable report={report} />
          )}

          {canViewSensitiveEvidence && report.report.centers.length > 0 && (
            <OperationalEvidencePanel
              report={report}
              token={token}
              canExport={canExportSensitiveEvidence}
            />
          )}

          <FormulaLegend
            formulaVersion={report.report.formulaVersion}
            approvalReference={report.report.approvalReference}
            currency={report.currency}
            presentation={report.moneyPresentation}
            asOfDate={report.report.asOfDate}
            previousMonthAligned={report.report.periods.previousMonthAligned}
          />
        </>
      )}
    </section>
  );
}

function OperationalEvidencePanel({
  report,
  token,
  canExport,
}: {
  report: BusinessDailyRevenueResponse;
  token?: string;
  canExport: boolean;
}) {
  const [metricKey, setMetricKey] = useState<BusinessOperationalEvidenceMetric>('IVF_ADVICE');
  const [center, setCenter] = useState('');
  const [evidence, setEvidence] = useState<BusinessOperationalEvidence | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [failure, setFailure] = useState('');

  useEffect(() => {
    setEvidence(null);
    setFailure('');
    if (center && !report.report.centers.some((item) => item.centerCode === center)) {
      setCenter('');
    }
  }, [center, report.selection.from, report.selection.to, report.report.centers]);

  const filters = {
    metricKey,
    from: report.selection.from,
    to: report.selection.to,
    centers: center
      ? [center]
      : report.report.centers.map((item) => item.centerCode),
    take: 250,
  };

  const loadEvidence = async () => {
    setLoading(true);
    setFailure('');
    try {
      setEvidence(await getBusinessOperationalEvidence(token, filters));
    } catch (error) {
      setEvidence(null);
      setFailure(reportError(error, 'Could not load patient-level reconciliation').message);
    } finally {
      setLoading(false);
    }
  };

  const exportEvidence = async () => {
    setExporting(true);
    setFailure('');
    try {
      const exported = await exportBusinessOperationalEvidence(token, filters);
      downloadBlob(
        exported.blob,
        exported.fileName || `Ferty9_${metricKey}_patient_reconciliation_${report.selection.from}_${report.selection.to}.xlsx`,
      );
    } catch (error) {
      setFailure(reportError(error, 'Could not export patient-level reconciliation').message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <details className="enterprise-card daily-revenue-evidence-card">
      <summary>
        <span>
          <strong>Count reconciliation</strong>
          <small>Match Registrations, IVF Advice or Fresh OPU to patient-level source evidence.</small>
        </span>
        <span className="business-source-count">Restricted access</span>
      </summary>
      <div className="daily-revenue-evidence-content">
        <p className="business-source-secondary">
          This view does not change dashboard figures. It lists the governed events already counted for the selected period and permitted centres, so they can be compared by Patient ID or MRN with the source-system list.
        </p>
        <div className="daily-revenue-evidence-filters">
          <label className="enterprise-field">
            <span>Count to reconcile</span>
            <select value={metricKey} onChange={(event) => { setMetricKey(event.target.value as BusinessOperationalEvidenceMetric); setEvidence(null); }}>
              <option value="IVF_ADVICE">IVF Advice</option>
              <option value="REGISTRATIONS">New Patient Registrations</option>
              <option value="FRESH_OPU">Fresh OPU</option>
            </select>
          </label>
          <label className="enterprise-field">
            <span>Centre</span>
            <select value={center} onChange={(event) => { setCenter(event.target.value); setEvidence(null); }}>
              <option value="">All displayed centres</option>
              {report.report.centers.map((item) => (
                <option key={item.centerCode} value={item.centerCode}>{shortCenterName(item.displayName)}</option>
              ))}
            </select>
          </label>
          <div className="daily-revenue-evidence-actions">
            <button className="btn btn-primary" type="button" onClick={() => void loadEvidence()} disabled={loading}>
              {loading ? 'Loading details…' : 'Load patient rows'}
            </button>
            {canExport && (
              <button className="btn btn-outline" type="button" onClick={() => void exportEvidence()} disabled={exporting}>
                {exporting ? 'Preparing Excel…' : 'Export patient Excel'}
              </button>
            )}
          </div>
        </div>
        <p className="business-source-secondary">
          Period: {displayDate(report.selection.from)} to {displayDate(report.selection.to)}. Every view and export is centre-scoped and audited.
        </p>
        {failure && <div className="daily-revenue-evidence-message is-error" role="alert">{failure}</div>}
        {evidence && (
          <>
            <div className="daily-revenue-evidence-result">
              <strong>{evidence.total.toLocaleString('en-IN')} counted event{evidence.total === 1 ? '' : 's'}</strong>
              <span>{evidence.rows.length < evidence.total ? `Showing first ${evidence.rows.length.toLocaleString('en-IN')}; Excel contains the complete list.` : 'All matching rows are shown.'}</span>
            </div>
            {!evidence.patientDetailsAvailable && (
              <div className="daily-revenue-evidence-message">
                Patient identifiers were not retained by the earlier source configuration. Event references are shown now; Patient ID, MRN and name will populate after the next governed source refresh.
              </div>
            )}
            <div className="enterprise-table-wrap">
              <table className="enterprise-table daily-revenue-evidence-table">
                <thead><tr><th>Date / centre</th><th>Patient</th><th>Event details</th><th>Source</th></tr></thead>
                <tbody>
                  {evidence.rows.length ? evidence.rows.map((row) => (
                    <tr key={row.id}>
                      <td><strong>{displayDate(row.businessDate)}</strong><span className="business-source-secondary">{shortCenterName(row.centerName)} · {row.centerCode}</span></td>
                      <td><strong>{row.patientName || 'Name pending next refresh'}</strong><span className="business-source-secondary">Patient ID: {row.patientId || row.patientReference || 'Pending'}</span><span className="business-source-secondary">MRN: {row.clinicMrn || 'Pending'}</span></td>
                      <td><strong>{row.classification || 'Counted event'}</strong><span className="business-source-secondary">Event: {row.eventReference || 'Not available'}</span>{row.invoiceReference && <span className="business-source-secondary">Invoice: {row.invoiceReference}</span>}</td>
                      <td>{row.sourceName}<span className="business-source-secondary">Observed {displayDateTime(row.lastObservedAt)}</span></td>
                    </tr>
                  )) : <tr><td colSpan={4} className="empty-cell">No counted events match this selection.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </details>
  );
}

function ReportDataStatus({ report }: { report: BusinessDailyRevenueResponse }) {
  const presentation: Record<BusinessDailyRevenueResponse['status'], {
    label: string;
    tone: string;
    description: string;
  }> = {
    READY: {
      label: 'Data Ready',
      tone: 'ready',
      description: 'Latest validated figures are ready for the selected period.',
    },
    STALE: {
      label: 'Data Incomplete',
      tone: 'validating',
      description: 'Sources do not share a complete common data-through date for this report.',
    },
    DEGRADED: {
      label: 'Data Validating',
      tone: 'degraded',
      description: 'Latest validated figures are shown while a source condition is reviewed. Refresh timing and earlier run history remain available in Operational Monitoring.',
    },
    PARTIAL_DATA: {
      label: 'Data Partial',
      tone: 'partial',
      description: 'Only part of the required financial or operational data is available.',
    },
    NO_DATA: {
      label: 'No Data',
      tone: 'no-data',
      description: 'No report data is available for this selection.',
    },
  };
  const status = presentation[report.status];
  const details = reportStatusTooltip(report, status.description);
  return (
    <span
      className={`daily-revenue-data-status ${status.tone}`}
      role="status"
      tabIndex={0}
      aria-label={`${status.label}. ${details}`}
      data-tooltip={details}
    >
      {status.label}
    </span>
  );
}

function SourceCoverageNotice({
  report,
}: {
  report: BusinessDailyRevenueResponse;
}) {
  if (
    report.status === 'NO_DATA' ||
    report.freshness.completeThroughRequestedDate
  ) {
    return null;
  }
  const common = report.freshness.commonDataThroughDate
    ? displayDate(report.freshness.commonDataThroughDate)
    : 'unavailable';
  const requested = displayDate(report.freshness.requestedThroughDate);
  const supplementalWarnings = report.warnings.filter(
    (warning) => !warning.startsWith('Incomplete source coverage:'),
  );
  return (
    <div className="daily-revenue-notice error" role="alert">
      <strong>Incomplete source coverage</strong>
      <span>
        Figures are common only through {common}; this report requested data through {requested}.
        {supplementalWarnings.length > 0
          ? ` ${supplementalWarnings.join(' ')}`
          : ''}
      </span>
    </div>
  );
}

function reportStatusTooltip(
  report: BusinessDailyRevenueResponse,
  description: string,
): string {
  if (report.status !== 'STALE' && report.status !== 'DEGRADED') {
    return description;
  }

  const affectedSources = report.freshness.sources.filter((source) =>
    report.status === 'STALE'
      ? source.isStale ||
        source.missedExpectedRefresh ||
        source.dataThroughDate !== report.freshness.latestDataThroughDate
      : !['HEALTHY', 'INACTIVE'].includes(source.state),
  );
  const sourceDetails = affectedSources.slice(0, 2).map((source) => {
    const timing = source.lastSuccessAt
      ? `last refreshed ${displayDateTime(source.lastSuccessAt)}`
      : 'awaiting its first successful refresh';
    const dataThrough = source.dataThroughDate
      ? `data through ${displayDate(source.dataThroughDate)}`
      : 'validated-through date not yet available';
    return `${source.sourceName}: ${dataThrough}; ${timing}.`;
  });
  if (affectedSources.length > 2) {
    sourceDetails.push(`${affectedSources.length - 2} additional source${affectedSources.length === 3 ? '' : 's'} can be reviewed in Operational Monitoring.`);
  }

  const earlierRunItems = report.freshness.sources.filter((source) => {
    if (!source.lastErrorAt || !source.lastSuccessAt) return false;
    return new Date(source.lastErrorAt).getTime() < new Date(source.lastSuccessAt).getTime();
  }).length;
  if (earlierRunItems > 0) {
    sourceDetails.push(`${earlierRunItems} earlier source run item${earlierRunItems === 1 ? '' : 's'} remain available in Operational Monitoring for audit history.`);
  }

  return [description, ...sourceDetails].join(' ');
}

function ActualRangeTable({ report }: { report: BusinessDailyRevenueResponse }) {
  const rows = report.rangeActuals?.centers || [];
  return (
    <section className="enterprise-card" aria-labelledby="actual-range-heading">
      <div className="daily-revenue-section-heading">
        <div><p className="eyebrow">Actual-only period</p><h3 id="actual-range-heading">Centre actuals</h3></div>
        <span>Targets and projected landing: Not applicable</span>
      </div>
      <div className="table-scroll"><table><thead><tr><th>Centre</th><th>Revenue</th><th>Registrations</th><th>IVF Advice</th><th>Fresh OPU</th></tr></thead><tbody>
        {rows.map((row) => <tr key={row.centerId}><td>{shortCenterName(row.displayName)}<br /><small>{row.centerCode}</small></td><td>{formatValue(row.revenueRupees, 'money', report.currency, report.moneyPresentation)}</td><td>{row.registrations}</td><td>{row.ivfAdvice}</td><td>{row.freshOpu}</td></tr>)}
      </tbody></table></div>
    </section>
  );
}

function RevenueTable({ report }: { report: BusinessDailyRevenueResponse }) {
  return (
    <section className="enterprise-card daily-revenue-table-card" aria-labelledby="center-performance-heading">
      <div className="daily-revenue-section-heading">
        <div>
          <h3 id="center-performance-heading">Performance by centre</h3>
        </div>
      </div>
      <div className="daily-revenue-table-wrap" tabIndex={0} aria-label="Scrollable Daily Revenue centre table">
        <table className="daily-revenue-table">
          <caption className="sr-only">
            Daily Revenue Snapshot measures by permitted centre as on {displayDate(report.report.asOfDate)}
          </caption>
          <colgroup>
            <col className="daily-revenue-col-centre" />
            <col className="daily-revenue-col-age" />
            <col className="daily-revenue-col-measure" />
            <col className="daily-revenue-col-period" span={2} />
            <col className="daily-revenue-col-period" />
            <col className="daily-revenue-col-target" />
            <col className="daily-revenue-col-mtd" />
            <col className="daily-revenue-col-landing" />
            <col className="daily-revenue-col-growth" span={2} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Centre</th>
              <th scope="col">Launch / age</th>
              <th scope="col">Measure</th>
              <th scope="col">{report.report.periods.q4Benchmark.label}</th>
              <th scope="col">{report.report.periods.q1Benchmark.label}</th>
              <th scope="col">{report.report.periods.previousMonth.label}</th>
              <th scope="col">Target</th>
              <th scope="col">MTD</th>
              <th scope="col">Projected landing</th>
              <th scope="col">Growth vs LM %</th>
              <th scope="col">Growth vs Q4 %</th>
            </tr>
          </thead>
          <tbody>
            {report.report.centers.flatMap((center) => {
              const metrics = centerRows(
                center,
                report.moneyPresentation.summaryDisplayScale,
              );
              return metrics.map((metric, index) => (
                <tr
                  key={`${center.centerId}:${metric.key}`}
                  className={index === metrics.length - 1 ? 'daily-revenue-centre-end' : undefined}
                >
                  {index === 0 && (
                    <>
                      <th scope="rowgroup" rowSpan={metrics.length} className="daily-revenue-centre-cell daily-revenue-group-cell">
                        <strong>{shortCenterName(center.displayName)}</strong>
                        <span>Code {center.centerCode}</span>
                      </th>
                      <td rowSpan={metrics.length} className="daily-revenue-age-cell daily-revenue-group-cell">
                        <strong>{center.launchDate ? displayDate(center.launchDate) : 'Not set'}</strong>
                        <span>{center.centerAgeYears === null ? 'Age unavailable' : `${formatCount(center.centerAgeYears)} years`}</span>
                      </td>
                    </>
                  )}
                  <th scope="row" className="daily-revenue-measure-cell">
                    <span>{metric.label}</span>
                    <small>{metric.shortLabel}</small>
                  </th>
                  <ValueCell value={metric.value.q4Average} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} wholePercent wholeCount />
                  <ValueCell value={metric.value.q1Average} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} wholePercent wholeCount />
                  <ValueCell value={metric.value.previousMonthActual} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} />
                  <ValueCell value={metric.value.target} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} />
                  <ValueCell value={metric.value.mtdActual} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} highlight="mtd" />
                  <ValueCell value={metric.value.projectedLanding} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} highlight="landing" />
                  <GrowthCell value={metric.value.growthVsPreviousMonth} />
                  <GrowthCell value={metric.value.growthVsQ4} />
                </tr>
              ));
            })}
            {centerRows(
              report.report.grandTotal,
              report.moneyPresentation.summaryDisplayScale,
            ).map((metric, index, rows) => (
              <tr
                key={`grand-total:${metric.key}`}
                className={`daily-revenue-grand-total${index === 0 ? ' daily-revenue-grand-start' : ''}${index === rows.length - 1 ? ' daily-revenue-grand-end' : ''}`}
              >
                {index === 0 && (
                  <>
                    <th
                      scope="rowgroup"
                      rowSpan={rows.length}
                      className="daily-revenue-centre-cell daily-revenue-group-cell daily-revenue-grand-label"
                    >
                      <strong>Grand Total</strong>
                      <span>{report.report.centers.length} permitted centres</span>
                    </th>
                    <td
                      rowSpan={rows.length}
                      className="daily-revenue-age-cell daily-revenue-group-cell daily-revenue-grand-label"
                    >
                      <strong>Combined</strong>
                      <span>All displayed centres</span>
                    </td>
                  </>
                )}
                <th scope="row" className="daily-revenue-measure-cell">
                  <span>{metric.label}</span>
                  <small>{metric.shortLabel}</small>
                </th>
                <ValueCell value={metric.value.q4Average} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} wholePercent wholeCount />
                <ValueCell value={metric.value.q1Average} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} wholePercent wholeCount />
                <ValueCell value={metric.value.previousMonthActual} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} />
                <ValueCell value={metric.value.target} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} />
                <ValueCell value={metric.value.mtdActual} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} highlight="mtd" />
                <ValueCell value={metric.value.projectedLanding} kind={metric.kind} currency={report.currency} presentation={report.moneyPresentation} highlight="landing" />
                <GrowthCell value={metric.value.growthVsPreviousMonth} />
                <GrowthCell value={metric.value.growthVsQ4} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ValueCell({
  value,
  kind,
  currency,
  presentation,
  highlight,
  wholePercent = false,
  wholeCount = false,
}: {
  value: number | null;
  kind: MetricRow['kind'];
  currency: string;
  presentation: BusinessMoneyPresentation;
  highlight?: string;
  wholePercent?: boolean;
  wholeCount?: boolean;
}) {
  return (
    <td className={highlight ? `is-${highlight}` : undefined}>
      {formatTableValue(value, kind, currency, presentation, wholePercent, wholeCount)}
    </td>
  );
}

function GrowthCell({ value }: { value: number | null }) {
  const formatted = formatGrowthPercent(value);
  return (
    <td className={`daily-revenue-growth ${formatted.tone}`}>
      <strong>{formatted.symbol} {formatted.text}</strong>
      <span className="sr-only">{growthLabel(value)}</span>
    </td>
  );
}

function FormulaLegend({
  formulaVersion,
  approvalReference,
  currency,
  presentation,
  asOfDate,
  previousMonthAligned,
}: {
  formulaVersion: string;
  approvalReference: string;
  currency: string;
  presentation: BusinessMoneyPresentation;
  asOfDate: string;
  previousMonthAligned: { start: string; end: string; label: string };
}) {
  return (
    <>
      <section className="enterprise-card daily-revenue-landing-guide" aria-labelledby="landing-guide-heading">
        <div className="daily-revenue-landing-guide-heading">
          <div>
            <p className="eyebrow">Forecast guide</p>
            <h3 id="landing-guide-heading">How projected landing works</h3>
          </div>
          <span>Uses the current month's achieved daily run rate</span>
        </div>
        <div className="daily-revenue-landing-cards">
          <article>
            <span>Step 1</span>
            <h4>Elapsed-month factor</h4>
            <p>Extends the achieved average daily run rate across every day in the selected month.</p>
            <code>Days in month / elapsed days</code>
            <small>Example on day 25 of a 31-day month: 31 / 25 = <strong>1.24 factor</strong></small>
          </article>
          <article>
            <span>Step 2</span>
            <h4>Projected Landing</h4>
            <p>Estimates the final result at the current month's achieved average daily run rate.</p>
            <code>Current MTD x elapsed-month factor</code>
            <small>Example: 775.7 L x 1.24 = <strong>961.9 L projected landing</strong></small>
          </article>
        </div>
        <p className="daily-revenue-landing-note">
          On the final day of a completed month, the factor is 1 and projected landing equals the completed actual. An approved management override still takes precedence when one is configured.
        </p>
      </section>
      <details className="enterprise-card daily-revenue-legend">
        <summary>Formula and unit guide</summary>
        <div>
          <dl>
            <div><dt>Revenue</dt><dd>All revenue is in {currency}. The source value is stored as the full rupee amount. This report shows it in {displayScaleLabel(presentation.summaryDisplayScale)}, and detailed exports use {displayScaleLabel(presentation.detailDisplayScale)}. For example, INR 1,250,000 is shown as 12.50 lakhs. No currency conversion is applied.</dd></div>
            <div><dt>Quarter averages</dt><dd>The Q4 and previous-quarter columns show an average month. Revenue and counts are the quarter total divided by the number of included months. Fresh OPU, Registrations and IVF Advice are shown as the nearest whole count, while the unrounded value is retained for calculations. Advice % and OPU Conversion % are calculated from the complete quarter totals and rounded to the nearest whole percent.</dd></div>
            <div><dt>Advice %</dt><dd>Compares the qualifying IVF Advice count with New Patient Registrations for the same centre and period. Formula: IVF Advice / Registrations x 100. A zero or missing Registration count is shown as unavailable.</dd></div>
            <div><dt>OPU Conversion %</dt><dd>Compares the governed Fresh OPU count with New Patient Registrations for the same centre and period. Formula: Fresh OPU / Registrations x 100. A zero or missing Registration count is shown as unavailable.</dd></div>
            <div><dt>Grand ratios</dt><dd>The Grand Total percentage is calculated from the combined counts for all displayed centres. It is not an average of the individual centre percentages.</dd></div>
            <div><dt>Growth vs LM %</dt><dd>Compares this month's actual through {displayDate(asOfDate)} with the same elapsed period last month, from {displayDate(previousMonthAligned.start)} through {displayDate(previousMonthAligned.end)}. Formula: (Current MTD / same-period last-month actual) - 1. Projected Landing is not used.</dd></div>
            <div><dt>Growth vs Q4 %</dt><dd>Compares this month's actual through {displayDate(asOfDate)} with the average result for the same number of days in January, February and March of the previous financial year's Q4. Formula: (Current MTD / aligned Q4 monthly average) - 1. Projected Landing is not used.</dd></div>
          </dl>
          <p>Formula version <code>{formulaVersion}</code> | approval <code>{approvalReference}</code></p>
        </div>
      </details>
    </>
  );
}

function landingSourceLabel(
  source: BusinessDailyRevenueMetric['landingSource'],
): string {
  switch (source) {
    case 'APPROVED_OVERRIDE':
      return 'Approved BR-0-09 override';
    case 'CURRENT_MTD_RUN_RATE':
      return 'Current MTD daily run rate';
    case 'PRIOR_YEAR_CURVE':
      return 'Prior-year performance curve';
    case 'OVERRIDE_REQUIRED':
      return 'Approved override required';
    case 'DISABLED':
      return 'Landing disabled';
    default:
      return 'Unavailable';
  }
}

function DailyRevenueSkeleton() {
  return (
    <div className="daily-revenue-skeleton" aria-hidden="true">
      <div className="daily-revenue-skeleton-kpis">
        {Array.from({ length: 6 }, (_, index) => <span key={index} />)}
      </div>
      <div className="daily-revenue-skeleton-table">
        {Array.from({ length: 7 }, (_, index) => <span key={index} />)}
      </div>
    </div>
  );
}

function centerRows(
  center: BusinessDailyRevenueCenter,
  moneyScale: BusinessMoneyPresentation['summaryDisplayScale'],
): MetricRow[] {
  return [
    { key: 'revenueRupees', label: 'Revenue', shortLabel: displayScaleLabel(moneyScale), kind: 'money', value: center.metrics.revenueRupees },
    { key: 'freshOpu', label: 'Fresh OPU', shortLabel: 'Count', kind: 'count', value: center.metrics.freshOpu },
    { key: 'registrations', label: 'Registrations', shortLabel: 'Count', kind: 'count', value: center.metrics.registrations },
    { key: 'ivfAdvice', label: 'IVF Advice', shortLabel: 'Count', kind: 'count', value: center.metrics.ivfAdvice },
    { key: 'adviceConversion', label: 'Advice %', shortLabel: 'IVF Advice ÷ registrations', kind: 'ratio', value: center.ratios.adviceConversion },
    { key: 'opuConversion', label: 'OPU Conversion %', shortLabel: 'Selected OPU ÷ registrations', kind: 'ratio', value: center.ratios.opuConversion },
  ];
}

function shortCenterName(displayName: string): string {
  return displayName.replace(/^Ferty9 Fertility Center\s*-\s*/i, '').trim();
}

function formatValue(
  value: number | null,
  kind: MetricRow['kind'],
  currency: string,
  presentation: BusinessMoneyPresentation,
): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (kind === 'money') {
    const scale = presentation.summaryDisplayScale;
    const suffix = displayScaleSuffix(scale);
    const formatted = (value / displayScaleDivisor(scale)).toLocaleString(
      'en-IN',
      {
        minimumFractionDigits: presentation.summaryDecimalPlaces,
        maximumFractionDigits: presentation.summaryDecimalPlaces,
      },
    );
    return `${currency} ${formatted}${suffix ? ` ${suffix}` : ''}`;
  }
  if (kind === 'ratio') return formatPercent(value).text;
  return formatCount(value);
}

function formatTableValue(
  value: number | null,
  kind: MetricRow['kind'],
  currency: string,
  presentation: BusinessMoneyPresentation,
  wholePercent = false,
  wholeCount = false,
): string {
  if (value === null || !Number.isFinite(value)) return '\u2014';
  if (kind !== 'money') {
    if (kind === 'ratio') return formatPercent(value, wholePercent ? 0 : 1).text;
    if (kind === 'count' && wholeCount) return Math.round(value).toLocaleString('en-IN');
    return formatValue(value, kind, currency, presentation);
  }
  return (value / displayScaleDivisor(presentation.summaryDisplayScale)).toLocaleString(
    'en-IN',
    {
      minimumFractionDigits: presentation.summaryDecimalPlaces,
      maximumFractionDigits: presentation.summaryDecimalPlaces,
    },
  );
}

function displayScaleDivisor(
  scale: BusinessMoneyPresentation['summaryDisplayScale'],
): number {
  if (scale === 'THOUSANDS') return 1_000;
  if (scale === 'LAKHS') return 100_000;
  if (scale === 'CRORES') return 10_000_000;
  return 1;
}

function displayScaleSuffix(
  scale: BusinessMoneyPresentation['summaryDisplayScale'],
): string {
  if (scale === 'THOUSANDS') return 'K';
  if (scale === 'LAKHS') return 'L';
  if (scale === 'CRORES') return 'Cr';
  return '';
}

function displayScaleLabel(
  scale: BusinessMoneyPresentation['summaryDisplayScale'],
): string {
  if (scale === 'THOUSANDS') return 'thousands';
  if (scale === 'LAKHS') return 'lakhs';
  if (scale === 'CRORES') return 'crores';
  return 'full units';
}

function formatCount(value: number): string {
  return value.toLocaleString('en-IN', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  });
}

function formatPercent(value: number | null, fractionDigits = 1): {
  text: string;
  tone: 'positive' | 'negative' | 'neutral';
  symbol: string;
} {
  if (value === null || !Number.isFinite(value)) {
    return { text: '—', tone: 'neutral', symbol: '•' };
  }
  const tone = value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral';
  return {
    text: `${(value * 100).toLocaleString('en-IN', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}%`,
    tone,
    symbol: value > 0 ? '↑' : value < 0 ? '↓' : '→',
  };
}

function formatGrowthPercent(value: number | null): {
  text: string;
  tone: 'positive' | 'negative' | 'neutral';
  symbol: string;
} {
  if (value === null || !Number.isFinite(value)) {
    return { text: '\u2014', tone: 'neutral', symbol: '\u2022' };
  }
  const tone = value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral';
  return {
    text: `${(value * 100).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}%`,
    tone,
    symbol: value > 0 ? '\u2191' : value < 0 ? '\u2193' : '\u2192',
  };
}

function growthLabel(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Growth unavailable';
  if (value > 0) return 'Increase';
  if (value < 0) return 'Decrease';
  return 'No change';
}

function reportError(
  error: unknown,
  fallbackTitle = 'Could not load Daily Revenue Snapshot',
) {
  const message =
    error instanceof Error ? error.message : 'The report request failed.';
  const activationPending =
    /not active|not approved|financial calendar|required|governance/i.test(
      message,
    );
  return {
    title: activationPending ? 'Report activation pending' : fallbackTitle,
    message,
    activationPending,
  };
}

function mergeCenters(
  current: Array<{ centerCode: string; displayName: string }>,
  incoming: BusinessDailyRevenueCenter[],
  replace: boolean,
) {
  const source = replace ? [] : current;
  return [
    ...new Map(
      [...source, ...incoming].map((center) => [
        center.centerCode,
        {
          centerCode: center.centerCode,
          displayName: center.displayName,
        },
      ]),
    ).values(),
  ].sort((left, right) => left.displayName.localeCompare(right.displayName));
}

function revenueFilters(
  mode: RevenuePeriodMode,
  asOf: string,
  date: string,
  month: string,
  from: string,
  to: string,
  centers: string[],
): BusinessDailyRevenueFilters {
  return {
    mode: mode === 'YTD' || mode === 'FY_YTD' ? 'CUSTOM_RANGE' : mode,
    centers,
    ...(mode === 'CURRENT_MONTH' ? { asOf } : {}),
    ...(mode === 'SPECIFIC_DATE' ? { date } : {}),
    ...(mode === 'CALENDAR_MONTH' ? { month } : {}),
    ...(mode === 'CUSTOM_RANGE' || mode === 'YTD' || mode === 'FY_YTD' ? { from, to } : {}),
  };
}

function displayDate(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime())) return 'Unavailable';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(date);
}

function displayDateTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'time unavailable';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(date);
}

function todayInIndia(): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Kolkata',
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function previousIsoDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() - 1);
  return parsed.toISOString().slice(0, 10);
}

function DownloadIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 20h14" />
    </svg>
  );
}
